import React, { useState, useEffect, useMemo, useRef } from 'react';

import { doc, updateDoc, collection, query, where, getDocs, getDoc, onSnapshot, serverTimestamp, addDoc, runTransaction, arrayUnion } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useToast } from '../../contexts/ToastContext';
import BookingStepper from './BookingStepper';

import { sendEmail, createNotification } from '../../utils/api';

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY || ''; 
export default function BookingDetail({ booking, user, db, storage, functions, navigateTo, fromPage, fromDate }) {
    const toast = useToast();
    const [fullBooking, setFullBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showClaimModal, setShowClaimModal] = useState(false);
    const [showContractModal, setShowContractModal] = useState(false);
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [showDeliveryConfirmModal, setShowDeliveryConfirmModal] = useState(false); 
    const [isPaying, setIsPaying] = useState(false);
    const [paymentError, setPaymentError] = useState('');
    const [actionError, setActionError] = useState(''); 
    const [bookingToAccept, setBookingToAccept] = useState(null);
    
    const [vendorNotes, setVendorNotes] = useState('');
    const [renterNotes, setRenterNotes] = useState(''); 
    const [savingNotes, setSavingNotes] = useState(false);

    const [savedCards, setSavedCards] = useState([]);
    const [selectedCard, setSelectedCard] = useState(null);

    const [myWeddings, setMyWeddings] = useState([]);
    const [myPlannerProjects, setMyPlannerProjects] = useState([]);
    const [myLegacyEvents, setMyLegacyEvents] = useState([]);

    const totalPaid = useMemo(() => (fullBooking?.payments || []).reduce((acc, p) => acc + p.amount, 0), [fullBooking]);
    const balanceDue = useMemo(() => (fullBooking?.totalCost || 0) - totalPaid, [fullBooking, totalPaid]);

    const canLeaveReview = useMemo(() => {
        if (!fullBooking || !user || user.uid !== fullBooking.renterId) return false;
        if (fullBooking.status !== 'returned' || fullBooking.reviewLeft) return false;

        const today = new Date();
        const returnDate = new Date(fullBooking.returnDate);
        const diffTime = today - returnDate;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        return diffDays > 0 && diffDays <= 14;
    }, [fullBooking, user]);

    useEffect(() => {
        let intervalId;
        const isPaymentSuccess = window.location.href.includes('success=true');
        const isGuest = user && user.isAnonymous;
        
        if ((isGuest || isPaymentSuccess) && fullBooking) {
            const pollStatus = async () => {
                try {
                    let newData = null;

                    if (isGuest && fullBooking.bookingNumber && fullBooking.renterEmail) {
                        const lookupGuestBooking = httpsCallable(functions, 'lookupGuestBooking');
                        const result = await lookupGuestBooking({ 
                            bookingNumber: fullBooking.bookingNumber, 
                            email: fullBooking.renterEmail 
                        });
                        newData = result.data;
                    } else if (!isGuest && fullBooking.id) {
                        const docRef = doc(db, "bookings", fullBooking.id);
                        const docSnap = await getDoc(docRef);
                        if (docSnap.exists()) {
                            newData = { id: docSnap.id, ...docSnap.data() };
                        }
                    }
                    
                    if (newData) {
                        setFullBooking(prev => {
                            if (prev.status !== newData.status || prev.contractStatus !== newData.contractStatus || (newData.payments && newData.payments.length !== (prev.payments?.length || 0))) {
                                if (isPaymentSuccess && newData.status !== 'awaitingPayment' && newData.status !== 'awaitingFinalPayment') {
                                    const cleanUrl = window.location.href.split('?')[0];
                                    window.history.replaceState(null, '', cleanUrl);
                                    setShowReceiptModal(true);
                                }
                                return { ...prev, ...newData };
                            }
                            return prev;
                        });
                    }
                } catch (e) {
                    console.error("Booking polling error (silenced):", e.message);
                }
            };

            if (isPaymentSuccess) {
                pollStatus();
                setTimeout(pollStatus, 1000);
                setTimeout(pollStatus, 3000);
            }

            intervalId = setInterval(pollStatus, 5000);
        }
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [user, fullBooking?.id, fullBooking?.bookingNumber, fullBooking?.renterEmail, functions, db]);

    useEffect(() => {
        if (user && !user.isAnonymous && user.uid !== fullBooking?.vendorId && fullBooking && (fullBooking.status === 'awaitingPayment' || fullBooking.status === 'awaitingFinalPayment')) {
            const fetchCards = async () => {
                try {
                    const getMethods = httpsCallable(functions, 'getCustomerPaymentMethods');
                    const result = await getMethods();
                    if (result.data && result.data.data && result.data.data.length > 0) {
                        setSavedCards(result.data.data);
                        setSelectedCard(result.data.data[0].id);
                    }
                } catch (error) {
                    console.error("Error fetching payment methods:", error);
                }
            };
            fetchCards();
        }
    }, [user, fullBooking?.status, functions]);

    useEffect(() => {
        if (user && !user.isAnonymous && !user.isVendor && db) {
            const qW = query(collection(db, "weddings"), where("ownerId", "==", user.uid));
            const unsubW = onSnapshot(qW, snap => setMyWeddings(snap.docs.map(d => ({id: d.id, ...d.data()}))));

            const qP = query(collection(db, "plannerProjects"), where("plannerId", "==", user.uid));
            const unsubP = onSnapshot(qP, snap => setMyPlannerProjects(snap.docs.map(d => ({id: d.id, ...d.data()}))));

            const qE = query(collection(db, `users/${user.uid}/events`));
            const unsubE = onSnapshot(qE, snap => setMyLegacyEvents(snap.docs.map(d => ({id: d.id, ...d.data()}))));

            return () => { unsubW(); unsubP(); unsubE(); };
        }
    }, [user, db]);

    const handleAssignEvent = async (field, value) => {
        if (!fullBooking || !db) return;
        try {
            await updateDoc(doc(db, "bookings", fullBooking.id), { [field]: value || null });
            
            if (field === 'plannerProjectId' && value) {
                const vendorSnap = await getDoc(doc(db, "users", fullBooking.vendorId));
                const vendorData = vendorSnap.exists() ? vendorSnap.data() : {};
                const listingSnap = await getDoc(doc(db, "listings", fullBooking.listingId));
                const listingCategory = listingSnap.exists() ? listingSnap.data().category : 'Rentals';
                
                const projectRef = doc(db, "plannerProjects", value);
                const projectSnap = await getDoc(projectRef);
                if (projectSnap.exists()) {
                     const projectData = projectSnap.data();
                     const existingVendors = projectData.vendors || [];
                     if (!existingVendors.some(v => v.id === fullBooking.vendorId)) {
                         const newVendor = {
                              id: fullBooking.vendorId,
                              name: vendorData.vendorName || vendorData.email || 'Unknown Vendor',
                              category: listingCategory,
                              status: 'Booked via Host-it!',
                              email: vendorData.email || '',
                              phone: vendorData.businessInfo?.phone || vendorData.phone || '',
                              cost: fullBooking.totalCost || 0,
                              paid: (fullBooking.payments || []).reduce((acc, p) => acc + p.amount, 0) || (fullBooking.status === 'paid' ? fullBooking.totalCost : 0),
                              contractSigned: fullBooking.contractStatus === 'signed'
                         };
                         await updateDoc(projectRef, { vendors: [...existingVendors, newVendor] });
                     }
                }
            }
            
            toast.success("Booking linked successfully!");
        } catch(err) {
            console.error("Failed to link booking", err);
            toast.error("Failed to link booking.");
        }
    };

    const handleSendContract = async (content) => {
        const bookingRef = doc(db, "bookings", fullBooking.id);
        await updateDoc(bookingRef, {
            contractStatus: 'sent',
            contractHtml: content,
            contractSentAt: serverTimestamp()
        });
        await createNotification(fullBooking.renterId, `Vendor has sent a contract for ${fullBooking.listingTitle}. Please sign.`, { page: 'bookingDetail', data: { booking: { id: fullBooking.id } } });
    };

    const handleSignContract = async (signature) => {
        const bookingRef = doc(db, "bookings", fullBooking.id);
        await updateDoc(bookingRef, {
            contractStatus: 'signed',
            contractSignature: signature,
            contractSignedAt: serverTimestamp()
        });
        await createNotification(fullBooking.vendorId, `Contract signed by ${signature} for ${fullBooking.listingTitle}.`, { page: 'bookingDetail', data: { booking: { id: fullBooking.id } } });
    };

    const handleConfirmDelivery = async () => {
        try {
            await updateDoc(doc(db, "bookings", fullBooking.id), { status: 'delivered' });
            await createNotification(fullBooking.vendorId, `Renter has confirmed receipt of ${fullBooking.listingTitle}.`, { page: 'bookingDetail', data: { booking: { id: fullBooking.id } } });
            setShowDeliveryConfirmModal(false);
            toast.success("Delivery confirmed!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to confirm delivery.");
        }
    };

    useEffect(() => {
        if (booking) {
            setFullBooking(prev => {
                if (prev && prev.id === booking.id) {
                    return prev;
                }
                return booking;
            });
            setVendorNotes(prev => prev || booking.vendorNotes || '');
            setRenterNotes(prev => prev || booking.renterNotes || '');
            setLoading(false);
        } else {
            setLoading(false);
            return;
        }
    
        if (user && !user.isAnonymous && booking.id && db) {
            const bookingRef = doc(db, "bookings", booking.id);
            const unsub = onSnapshot(bookingRef, (bookingSnap) => {
                if (bookingSnap.exists()) {
                    const bookingData = { id: bookingSnap.id, ...bookingSnap.data() };
                    
                    setFullBooking(bookingData);
                    if (bookingData.vendorNotes !== undefined) setVendorNotes(bookingData.vendorNotes);
                    if (bookingData.renterNotes !== undefined) setRenterNotes(bookingData.renterNotes); 
                }
            });
            return () => unsub();
        }
    }, [booking, db, user]);

    const handlePayNow = async (paymentType = 'initial') => {
        setIsPaying(true);
        setPaymentError('');

        if (paymentType.startsWith('installment_')) {
            try {
                const instId = parseInt(paymentType.split('_')[1], 10);
                const schedule = fullBooking.paymentTerms.schedule;
                const inst = schedule.find(i => i.id === instId);
                
                if (inst) {
                    const newSchedule = schedule.map(i => i.id === instId ? { ...i, status: 'paid' } : i);
                    const allPaid = newSchedule.every(i => i.status === 'paid');
                    
                    const paymentRecord = {
                        amount: inst.amount,
                        date: serverTimestamp(),
                        type: "Installment " + inst.index
                    };

                    await updateDoc(doc(db, "bookings", fullBooking.id), {
                        'paymentTerms.schedule': newSchedule,
                        status: allPaid ? 'paid' : 'awaitingPayment',
                        payments: arrayUnion(paymentRecord)
                    });
                    
                    toast.success("Installment " + inst.index + " paid successfully!");
                    setShowReceiptModal(true);
                }
            } catch (err) {
                setPaymentError(err.message || 'Could not process installment.');
            } finally {
                setIsPaying(false);
            }
            return;
        }

        try {
            if (selectedCard) {
                const processSavedCardPayment = httpsCallable(functions, 'processSavedCardPayment');
                await processSavedCardPayment({
                    bookingId: fullBooking.id,
                    paymentMethodId: selectedCard,
                    paymentType: paymentType
                });
                toast.success("Payment processed successfully!");
                setShowReceiptModal(true);
            } else {
                const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
                const origin = window.location.origin || `${window.location.protocol}//${window.location.host}`;
                const baseUrl = window.location.href.split('?')[0];
                const returnUrl = `${baseUrl}?success=true`;
                const cancelUrl = baseUrl;
                
                const result = await createCheckoutSession({
                    bookingId: fullBooking.id,
                    paymentType,
                    origin,
                    returnUrl,
                    cancelUrl
                });

                if (result.data && result.data.url) {
                    window.location.href = result.data.url;
                } else if (result.data && result.data.sessionId) {
                    throw new Error("Payment session created without redirect URL.");
                } else {
                    throw new Error("Unable to create payment session.");
                }
            }
        } catch (err) {
            console.error("Payment failed:", err);
            setPaymentError(err.message || 'Could not process payment.');
        } finally {
            setIsPaying(false);
        }
    };

    const handleBack = () => {
        navigateTo('dashboard', { viewedBookingId: booking.id });
    };
    
    const handlePrintOrderReceipt = () => {
        if (!fullBooking) return;
        const printWindow = window.open('', '_blank');
        const currentDate = new Date().toLocaleDateString();
        const refNumber = fullBooking.bookingNumber || fullBooking.id.slice(0,8).toUpperCase();
        
        const paymentsHtml = (fullBooking.payments || []).map(p => `
            <div class="details-row" style="color: #10b981;">
                <span>Payment (${new Date(p.date?.seconds * 1000 || Date.now()).toLocaleDateString()}) - <span style="text-transform: capitalize;">${p.type}</span></span>
                <span>-$${parseFloat(p.amount).toFixed(2)}</span>
            </div>
        `).join('');

        const subtotal = fullBooking.subtotal || (fullBooking.totalCost - (fullBooking.renterFee || 0) - (fullBooking.deliveryFee || 0) - (fullBooking.taxAmount || 0));

        printWindow.document.write(`
            <html>
                <head>
                    <title>Order Receipt - ${refNumber}</title>
                    <style>
                        body { font-family: 'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif; padding: 40px; color: #333; max-width: 600px; margin: 0 auto; -webkit-print-color-adjust: exact; }
                        .header { text-align: center; border-bottom: 2px solid #eee; padding-bottom: 20px; margin-bottom: 30px; }
                        .header h1 { margin: 0; color: #111827; font-size: 24px; letter-spacing: -0.5px; }
                        .header p { margin: 5px 0 0; color: #6b7280; font-size: 14px; text-transform: uppercase; letter-spacing: 1px; }
                        .details { margin-bottom: 20px; }
                        .details-row { display: flex; justify-content: space-between; border-bottom: 1px solid #f3f4f6; padding: 10px 0; font-size: 14px; }
                        .details-row span:first-child { font-weight: 600; color: #6b7280; }
                        .details-row span:last-child { font-weight: 500; color: #111827; text-align: right; }
                        .total-row { display: flex; justify-content: space-between; font-size: 18px; font-weight: 800; margin-top: 15px; padding-top: 15px; border-top: 2px solid #111827; color: #111827; }
                        .footer { text-align: center; margin-top: 50px; font-size: 13px; color: #9ca3af; }
                        .print-btn { margin-top: 30px; padding: 12px 24px; background: #111827; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 14px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
                        @media print { .no-print { display: none; } body { padding: 0; } }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>Order Receipt</h1>
                        <p>Host-it! Party Rentals</p>
                    </div>
                    <div class="details">
                        <div class="details-row"><span>Date Printed:</span><span>${currentDate}</span></div>
                        <div class="details-row"><span>Order Reference:</span><span>${refNumber}</span></div>
                        <div class="details-row"><span>Billed To:</span><span>${fullBooking.renterName || 'Guest'}<br><span style="font-size: 12px; font-weight: normal; color: #6b7280;">${fullBooking.renterEmail}</span></span></div>
                        <div class="details-row"><span>Item:</span><span>${fullBooking.quantity} x ${fullBooking.listingTitle}</span></div>
                        <div class="details-row"><span>Subtotal:</span><span>$${subtotal.toFixed(2)}</span></div>
                        ${fullBooking.deliveryFee > 0 ? `<div class="details-row"><span>Delivery Fee:</span><span>$${fullBooking.deliveryFee.toFixed(2)}</span></div>` : ''}
                        ${fullBooking.taxAmount > 0 ? `<div class="details-row"><span>Tax (${(fullBooking.taxRate * 100).toFixed(2)}%):</span><span>$${fullBooking.taxAmount.toFixed(2)}</span></div>` : ''}
                        <div class="details-row"><span>Service Fee:</span><span>$${(fullBooking.renterFee || 0).toFixed(2)}</span></div>
                    </div>
                    <div class="total-row"><span>Total:</span><span>$${(fullBooking.totalCost || 0).toFixed(2)}</span></div>
                    ${paymentsHtml}
                    ${balanceDue > 0 ? `<div class="total-row" style="color: #dc2626; font-size: 16px; border-top: 1px dashed #dc2626;"><span>Balance Due:</span><span>$${balanceDue.toFixed(2)}</span></div>` : ''}
                    <div class="footer">
                        <p>Thank you for your business!</p>
                        <button onclick="window.print()" class="print-btn no-print">Print / Save PDF</button>
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();
    };
    
    const savePrivateNotes = async () => {
        if (!fullBooking || !db) return;
        setSavingNotes(true);
        try {
            const updateData = {};
            if (user.uid === fullBooking.vendorId) updateData.vendorNotes = vendorNotes;
            else updateData.renterNotes = renterNotes;

            await updateDoc(doc(db, "bookings", fullBooking.id), updateData);
        } catch (err) {
            console.error("Failed to save notes", err);
        } finally {
            setSavingNotes(false);
        }
    };

    const handleOpenAcceptModal = () => {
        setBookingToAccept(fullBooking);
    };

    const handleConfirmAcceptBooking = async (bookingToConfirm, paymentTerms) => {
        setActionError('');
        try {
            await runTransaction(db, async (transaction) => {
                const listingRef = doc(db, "listings", bookingToConfirm.listingId);
                const bookingRef = doc(db, "bookings", bookingToConfirm.id);
                const listingDoc = await transaction.get(listingRef);

                if (!listingDoc.exists()) {
                    throw new Error("Listing no longer exists.");
                }

                const listingData = listingDoc.data();
                const newBookedDates = { ...listingData.bookedDates };
                let currentDate = new Date(bookingToConfirm.date);
                let stopDate = new Date(bookingToConfirm.returnDate);
                const optionName = bookingToConfirm.selectedOption || 'default';
                const inventorySource = listingData.options?.find(opt => opt.name === optionName) || listingData;

                while(currentDate <= stopDate) {
                    const dateString = currentDate.toISOString().split('T')[0];
                    if (!newBookedDates[dateString]) newBookedDates[dateString] = {};
                    
                    const currentBookedCount = newBookedDates[dateString][optionName] || 0;
                    if (currentBookedCount + bookingToConfirm.quantity > inventorySource.inventory) {
                        throw new Error(`Not enough inventory for "${listingData.title} - ${optionName}" on ${dateString}.`);
                    }
                    newBookedDates[dateString][optionName] = currentBookedCount + bookingToConfirm.quantity;
                    currentDate.setDate(currentDate.getDate() + 1);
                }

                const isNoPaymentRequired = paymentTerms.type === 'no_payment';

                transaction.update(listingRef, { bookedDates: newBookedDates });
                transaction.update(bookingRef, {
                    status: isNoPaymentRequired ? 'deposit-paid' : 'awaitingPayment',
                    paymentTerms: isNoPaymentRequired ? null : {
                        ...paymentTerms,
                        deadline: paymentTerms.deadline
                    }
                });
            });

            setBookingToAccept(null);
        } catch (error) {
            console.error("Error accepting booking:", error);
            setActionError(`Failed to accept booking: ${error.message}`);
            setBookingToAccept(null);
        }
    };

    const handleRequestFinalPayment = async () => {
        if (!fullBooking) return;
        const bookingRef = doc(db, "bookings", fullBooking.id);
        const totalPaid = (fullBooking.payments || []).reduce((acc, p) => acc + p.amount, 0);
        const finalAmount = fullBooking.totalCost - totalPaid;

        if (finalAmount <= 0) {
            await updateDoc(bookingRef, { status: 'paid' });
            return;
        }

        const deadline = new Date();
        deadline.setDate(deadline.getDate() + 2); 

        await updateDoc(bookingRef, {
            status: 'awaitingFinalPayment',
            paymentTerms: {
                ...fullBooking.paymentTerms,
                finalAmount: finalAmount,
                finalDeadline: deadline,
            },
        });
        
        await createNotification(fullBooking.renterId, `Final payment of $${finalAmount.toFixed(2)} is due for ${fullBooking.listingTitle}.`, { page: 'bookingDetail', data: { booking: { id: fullBooking.id } } });
    };

    const handleDeclineBooking = async () => {
        setActionError('');
        try {
            await runTransaction(db, async (transaction) => {
                const listingRef = doc(db, "listings", fullBooking.listingId);
                const bookingRef = doc(db, "bookings", fullBooking.id);
                const listingDoc = await transaction.get(listingRef);
                if (!listingDoc.exists()) throw new Error("Listing no longer exists.");
                
                const listingData = listingDoc.data();
                const newBookedDates = { ...listingData.bookedDates };
                
                const bufferDays = parseInt(listingData.bufferDays) || 0;
                let currentDate = new Date(fullBooking.date);
                let stopDate = new Date(fullBooking.returnDate);
                stopDate.setDate(stopDate.getDate() + bufferDays);

                while(currentDate <= stopDate) {
                    const dateString = currentDate.toISOString().split('T')[0];
                    if (newBookedDates[dateString]) {
                        newBookedDates[dateString] -= fullBooking.quantity;
                        if (newBookedDates[dateString] <= 0) {
                            delete newBookedDates[dateString];
                        }
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                }
                
                transaction.update(listingRef, { bookedDates: newBookedDates });
                transaction.update(bookingRef, { status: 'declined' });
            });

            await createNotification(
                fullBooking.renterId,
                `Your booking for ${fullBooking.listingTitle} was declined.`,
                { page: 'bookingDetail', data: { booking: { id: fullBooking.id } } }
            );
        } catch (error) {
            console.error("Error declining booking:", error);
            setActionError("Failed to decline booking. Please try again.");
        }
    };

    const handleMarkAsDelivered = async () => {
        const bookingRef = doc(db, "bookings", fullBooking.id);
        await updateDoc(bookingRef, { status: 'awaitingPlannerConfirmation' });
        await createNotification(fullBooking.renterId, `Your item, ${fullBooking.listingTitle}, has been marked as delivered!`, { page: 'bookingDetail', data: { booking: { id: fullBooking.id } } });
    };

    const getStatusClasses = (status) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'awaitingPayment':
            case 'awaitingFinalPayment':
            case 'awaitingPlannerConfirmation': return 'bg-orange-100 text-orange-800';
            case 'deposit-paid': return 'bg-teal-100 text-teal-800';
            case 'paid': return 'bg-green-100 text-green-800';
            case 'delivered': return 'bg-blue-100 text-blue-800';
            case 'awaitingPickup': return 'bg-purple-100 text-purple-800';
            case 'returned': return 'bg-green-100 text-green-800';
            case 'cancelled':
            case 'declined': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) return <div className="text-center p-10">Loading booking details...</div>;
    if (!fullBooking) return <div className="text-center p-10">Booking not found.</div>;

    const bookingNumber = fullBooking.bookingNumber || `BK-${fullBooking.id.slice(0, 6).toUpperCase()}`;

    const renderCardSelection = () => (
        savedCards.length > 0 && (
            <div className="mb-4">
                <p className="text-sm font-semibold mb-2">Select Payment Method:</p>
                <div className="space-y-2">
                    {savedCards.map(card => (
                        <label key={card.id} className={`flex items-center space-x-3 p-3 border rounded-md bg-white cursor-pointer transition hover:border-blue-400 ${selectedCard === card.id ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200'}`}>
                            <input 
                                type="radio" 
                                name="paymentMethod" 
                                checked={selectedCard === card.id} 
                                onChange={() => setSelectedCard(card.id)}
                                className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="capitalize font-medium text-gray-700">{card.card.brand} •••• {card.card.last4}</span>
                            <span className="text-xs text-gray-500 ml-auto">Expires {card.card.exp_month}/{card.card.exp_year}</span>
                        </label>
                    ))}
                    <label className={`flex items-center space-x-3 p-3 border rounded-md bg-white cursor-pointer transition hover:border-blue-400 ${selectedCard === null ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200'}`}>
                        <input 
                            type="radio" 
                            name="paymentMethod" 
                            checked={selectedCard === null} 
                            onChange={() => setSelectedCard(null)}
                            className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="font-medium text-gray-700">Use a different card</span>
                    </label>
                </div>
            </div>
        )
    );

    return (
        <div className="">
            {showClaimModal && <InsuranceClaimModal booking={fullBooking} onClose={() => setShowClaimModal(false)} user={user} db={db} storage={storage} />}
            {showReceiptModal && <PaymentReceiptModal booking={fullBooking} onClose={() => setShowReceiptModal(false)} />}
            {showReviewModal && <LeaveReviewModal booking={fullBooking} onClose={() => setShowReviewModal(false)} user={user} db={db} />}
            {showInvoiceModal && <InvoiceModal booking={fullBooking} user={user} onClose={() => setShowInvoiceModal(false)} db={db} />}
            {showContractModal && <ContractModal booking={fullBooking} user={user} onClose={() => setShowContractModal(false)} onSend={handleSendContract} onSign={handleSignContract} />}
            {bookingToAccept && <AcceptBookingModal booking={bookingToAccept} onClose={() => setBookingToAccept(null)} onConfirm={handleConfirmAcceptBooking} />}
            {showReturnModal && <ReturnConfirmationModal booking={fullBooking} onClose={() => setShowReturnModal(false)} db={db} />}
            {showDeliveryConfirmModal && <DeliveryConfirmationModal booking={fullBooking} onClose={() => setShowDeliveryConfirmModal(false)} onConfirm={handleConfirmDelivery} />}
            
            {user && !user.isAnonymous && (
                <button onClick={handleBack} className="mb-6 flex items-center text-gray-600 hover:text-pink-500 font-semibold">
                    &lt; Back to Dashboard
                </button>
            )}
            
            <div className="bg-white p-8 min-h-full rounded-lg shadow-sm border border-gray-100">
                <div className="flex justify-between items-start mb-6 pb-6 border-b">
                    <div>
                        <h2 className="text-3xl font-bold">Order #{bookingNumber}</h2>
                        <p className="text-gray-500">Placed on {fullBooking.createdAt && (fullBooking.createdAt.seconds || fullBooking.createdAt._seconds) ? new Date((fullBooking.createdAt.seconds || fullBooking.createdAt._seconds) * 1000).toLocaleDateString() : 'Date not available'}</p>
                    </div>
                    <div>
                        <span className={`px-3 py-1 text-sm font-bold rounded-full ${getStatusClasses(fullBooking.status)}`}>
                            {fullBooking.status ? fullBooking.status.replace(/([A-Z])/g, ' $1').trim() : 'Loading Status...'}
                        </span>
                    </div>
                </div>

                <BookingStepper status={fullBooking.status} />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-6">
                    <div className="md:col-span-2 space-y-6">
                        {/* Order Summary */}
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <h3 className="text-lg font-semibold">Order Summary</h3>
                                <button onClick={handlePrintOrderReceipt} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 font-semibold transition bg-blue-50 px-3 py-1 rounded-md">
                                    Print Receipt
                                </button>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <div className="flex justify-between border-b pb-2">
                                    <span className="font-semibold">{fullBooking.listingTitle} (x{fullBooking.quantity})</span>
                                    <span>${(fullBooking.subtotal || (fullBooking.totalCost - (fullBooking.renterFee || 0) - (fullBooking.deliveryFee || 0) - (fullBooking.insuranceFee || 0) - (fullBooking.taxAmount || 0))).toFixed(2)}</span>
                                </div>
                                {fullBooking.deliveryFee > 0 && (
                                    <div className="flex justify-between pt-2 text-sm text-gray-600 border-b pb-2">
                                        <span>Delivery Fee</span>
                                        <span>${fullBooking.deliveryFee.toFixed(2)}</span>
                                    </div>
                                )}
                                {fullBooking.insuranceFee > 0 && (
                                    <div className="flex justify-between pt-2 text-sm text-blue-600 border-b pb-2 font-medium">
                                        <span>Damage Protection</span>
                                        <span>${fullBooking.insuranceFee.toFixed(2)}</span>
                                    </div>
                                )}
                                {fullBooking.taxAmount > 0 && (
                                    <div className="flex justify-between pt-2 text-sm text-gray-600 border-b pb-2">
                                        <span>Tax ({(fullBooking.taxRate * 100).toFixed(2)}%)</span>
                                        <span>${fullBooking.taxAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between pt-2 text-sm text-gray-600">
                                    <span>Service Fee</span>
                                    <span>${(fullBooking.renterFee || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between font-bold text-lg pt-2 mt-2 border-t">
                                    <span>Total</span>
                                    <span>${(fullBooking.totalCost || 0).toFixed(2)}</span>
                                </div>
                                {totalPaid > 0 && (
                                    <>
                                        <div className="flex justify-between pt-2 text-sm text-green-600">
                                            <span>Paid</span>
                                            <span>-${totalPaid.toFixed(2)}</span>
                                        </div>
                                        {balanceDue > 0.01 && (
                                            <div className="flex justify-between font-bold text-lg pt-2 mt-2 border-t border-dashed border-red-300 text-red-600">
                                                <span>Balance Due</span>
                                                <span>${balanceDue.toFixed(2)}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Fulfillment Details */}
                        <div>
                            <h3 className="text-lg font-semibold mb-2">Fulfillment Details</h3>
                            <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                                <p><strong>Method:</strong> <span className="capitalize">{fullBooking.deliveryMethod}</span></p>
                                {fullBooking.deliveryMethod === 'delivery' && (
                                    <>
                                        <p><strong>Address:</strong> {fullBooking.deliveryAddress}</p>
                                        {fullBooking.deliveryAddress2 && <p><strong>Apt/Suite:</strong> {fullBooking.deliveryAddress2}</p>}
                                        <p><strong>Requested Time:</strong> {fullBooking.deliveryTime ? new Date(`1970-01-01T${fullBooking.deliveryTime}`).toLocaleTimeString([], {hour: 'numeric', minute:'2-digit'}) : 'Flexible'}</p>
                                    </>
                                )}
                                <p><strong>Rental Dates:</strong> {fullBooking.date} to {fullBooking.returnDate}</p>
                            </div>
                        </div>
                        
                        {/* Insurance Details */}
                        {fullBooking.hasPlatformInsurance && (
                            <div>
                                <h3 className="text-lg font-semibold mb-2">Damage Protection</h3>
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 text-sm">
                                    <p className="font-semibold text-blue-900 mb-1 flex items-center gap-1.5">
                                        Host-it! Damage Guarantee Active
                                    </p>
                                    <p className="text-blue-700 mb-3">This booking is protected against accidental damage and stains. (Excludes theft/loss).</p>
                                    {!fullBooking.claimStatus ? (
                                        <button onClick={() => setShowClaimModal(true)} className="bg-white text-blue-700 border border-blue-200 px-4 py-2 rounded-md font-semibold hover:bg-blue-50 transition shadow-sm w-full sm:w-auto">
                                            File a Claim
                                        </button>
                                    ) : (
                                        <div className="bg-white border border-blue-200 px-3 py-2 rounded-md font-bold text-blue-800 inline-block">
                                            Claim Status: {fullBooking.claimStatus.toUpperCase()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="space-y-6">
                        {/* Renter Information */}
                        <div>
                            <h3 className="text-lg font-semibold mb-2">Renter Information</h3>
                            <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-1">
                                <p className="font-bold">{fullBooking.renterName}</p>
                                <p className="text-gray-600"><a href={`mailto:${fullBooking.renterEmail}`} className="hover:text-pink-600 hover:underline transition">{fullBooking.renterEmail}</a></p>
                                {fullBooking.renterPhone && <a href={`tel:${fullBooking.renterPhone}`} className="text-gray-600 hover:text-pink-600 hover:underline block">{fullBooking.renterPhone}</a>}
                            </div>
                        </div>

                        {/* Event Linking Section (Renters Only) */}
                        {user && user.uid !== fullBooking.vendorId && (myWeddings.length > 0 || myPlannerProjects.length > 0 || myLegacyEvents.length > 0) && (
                            <div>
                                <h3 className="text-lg font-semibold mb-2">Event Organization</h3>
                                <div className="bg-purple-50 p-4 rounded-lg border border-purple-100 space-y-4">
                                    <p className="text-sm text-purple-800 font-medium">Link this booking to a project or site to keep your rentals organized.</p>
                                    
                                    {myPlannerProjects.length > 0 && (
                                        <div>
                                            <label className="block text-xs font-bold text-purple-700 uppercase mb-1">Planner Pro Project</label>
                                            <select 
                                                value={fullBooking.plannerProjectId || ''} 
                                                onChange={(e) => handleAssignEvent('plannerProjectId', e.target.value)}
                                                className="w-full p-2.5 rounded-lg border border-purple-200 text-sm focus:ring-2 focus:ring-purple-500 bg-white outline-none"
                                            >
                                                <option value="">-- None --</option>
                                                {myPlannerProjects.map(p => <option key={p.id} value={p.id}>{p.clientNames}</option>)}
                                            </select>
                                        </div>
                                    )}

                                    {myWeddings.length > 0 && (
                                        <div>
                                            <label className="block text-xs font-bold text-purple-700 uppercase mb-1">Wedding Website</label>
                                            <select 
                                                value={fullBooking.weddingId || ''} 
                                                onChange={(e) => handleAssignEvent('weddingId', e.target.value)}
                                                className="w-full p-2.5 rounded-lg border border-purple-200 text-sm focus:ring-2 focus:ring-purple-500 bg-white outline-none"
                                            >
                                                <option value="">-- None --</option>
                                                {myWeddings.map(w => <option key={w.id} value={w.id}>{w.title}</option>)}
                                            </select>
                                        </div>
                                    )}
                                    
                                    {myLegacyEvents.length > 0 && (
                                        <div>
                                            <label className="block text-xs font-bold text-purple-700 uppercase mb-1">Basic Event</label>
                                            <select 
                                                value={fullBooking.eventId || ''} 
                                                onChange={(e) => handleAssignEvent('eventId', e.target.value)}
                                                className="w-full p-2.5 rounded-lg border border-purple-200 text-sm focus:ring-2 focus:ring-purple-500 bg-white outline-none"
                                            >
                                                <option value="">-- None --</option>
                                                {myLegacyEvents.map(e => <option key={e.id} value={e.id}>{e.title}</option>)}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Private Notes Sections */}
                        {user && user.uid === fullBooking.vendorId && (
                            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                                <h3 className="font-bold text-yellow-800 mb-2 flex items-center gap-2">
                                    Private Vendor Notes
                                </h3>
                                <textarea 
                                    className="w-full p-3 border border-yellow-300 rounded bg-white text-sm focus:ring-2 focus:ring-yellow-500 outline-none" 
                                    placeholder="Internal notes about this order..."
                                    rows="3"
                                    value={vendorNotes}
                                    onChange={(e) => setVendorNotes(e.target.value)}
                                    onBlur={savePrivateNotes}
                                />
                                <p className="text-xs text-yellow-700 mt-1 text-right">
                                    {savingNotes ? 'Saving...' : 'Auto-saved'}
                                </p>
                            </div>
                        )}

                        {user && user.uid !== fullBooking.vendorId && (
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                                    My Personal Notes
                                </h3>
                                <textarea 
                                    className="w-full p-3 border border-blue-300 rounded bg-white text-sm focus:ring-2 focus:ring-blue-500 outline-none" 
                                    placeholder="Jot down notes for yourself..."
                                    rows="3"
                                    value={renterNotes}
                                    onChange={(e) => setRenterNotes(e.target.value)}
                                    onBlur={savePrivateNotes}
                                />
                                <p className="text-xs text-blue-700 mt-1 text-right">
                                    {savingNotes ? 'Saving...' : 'Auto-saved'}
                                </p>
                            </div>
                        )}

                        {/* Actions - For Vendors */}
                        {user && user.uid === fullBooking.vendorId && (
                            <div>
                                <h3 className="text-lg font-semibold mb-2">Actions</h3>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <BookingActions 
                                        booking={fullBooking}
                                        onAcceptBooking={handleOpenAcceptModal}
                                        onDeclineBooking={handleDeclineBooking}
                                        onMarkAsDelivered={handleMarkAsDelivered}
                                        onOpenReturnModal={() => setShowReturnModal(true)}
                                        onOpenInvoiceModal={() => setShowInvoiceModal(true)}
                                        onRequestFinalPayment={handleRequestFinalPayment}
                                        onOpenContractModal={() => setShowContractModal(true)}
                                    />
                                    {actionError && <p className="text-red-500 text-sm mt-3 font-semibold text-right">{actionError}</p>}
                                </div>
                            </div>
                        )}

                        {/* Payment Button - For Renters */}
                        {user && user.uid !== fullBooking.vendorId && fullBooking.status === 'awaitingPayment' && (
                             <div>
                                <h3 className="text-lg font-semibold mb-2">Payment Required</h3>
                                <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
                                    <p className="font-semibold">Your booking is accepted!</p>
                                    
                                    {fullBooking.paymentTerms?.type === 'installments' ? (
                                        <div className="mt-4 mb-4">
                                            <p className="text-sm text-gray-700 mb-2 font-bold">Payment Schedule:</p>
                                            <div className="space-y-2">
                                                {fullBooking.paymentTerms.schedule.map(inst => (
                                                    <div key={inst.id} className={`flex justify-between items-center p-3 rounded-md border ${inst.status === 'paid' ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
                                                        <div>
                                                            <p className={`font-semibold text-sm ${inst.status === 'paid' ? 'text-green-800 line-through' : 'text-gray-900'}`}>Payment {inst.index}: ${(inst.amount || 0).toFixed(2)}</p>
                                                            <p className="text-xs text-gray-500">Due: {inst.deadline?.seconds || inst.deadline?._seconds ? new Date((inst.deadline.seconds || inst.deadline._seconds) * 1000).toLocaleDateString() : new Date(inst.deadline).toLocaleDateString()}</p>
                                                        </div>
                                                        <span className={`text-xs font-bold px-2 py-1 rounded-full uppercase ${inst.status === 'paid' ? 'bg-green-200 text-green-800' : 'bg-orange-200 text-orange-800'}`}>{inst.status}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-700 mt-1 mb-4">
                                            Please pay <span className="font-bold">${(fullBooking.paymentTerms?.amount || 0).toFixed(2)}</span> by <span className="font-bold">{
                                                fullBooking.paymentTerms?.deadline 
                                                    ? (fullBooking.paymentTerms.deadline.seconds || fullBooking.paymentTerms.deadline._seconds 
                                                        ? new Date((fullBooking.paymentTerms.deadline.seconds || fullBooking.paymentTerms.deadline._seconds) * 1000).toLocaleDateString() 
                                                        : new Date(fullBooking.paymentTerms.deadline).toLocaleDateString()) 
                                                    : 'the deadline'
                                            }</span> to confirm your reservation.
                                        </p>
                                    )}
                                    
                                    {renderCardSelection()}

                                    <button 
                                        onClick={() => {
                                            if (fullBooking.paymentTerms?.type === 'installments') {
                                                const nextPending = fullBooking.paymentTerms.schedule.find(i => i.status === 'pending');
                                                if (nextPending) handlePayNow("installment_" + nextPending.id);
                                            } else {
                                                handlePayNow('initial');
                                            }
                                        }} 
                                        disabled={isPaying || (fullBooking.paymentTerms?.type === 'installments' && !fullBooking.paymentTerms.schedule.find(i => i.status === 'pending'))} 
                                        className="w-full bg-green-500 text-white py-2 rounded-lg font-semibold hover:bg-green-600 transition disabled:bg-gray-400"
                                    >
                                        {isPaying ? 'Processing...' : (
                                            fullBooking.paymentTerms?.type === 'installments' 
                                            ? (() => {
                                                const nextPending = fullBooking.paymentTerms.schedule.find(i => i.status === 'pending');
                                                return nextPending ? (selectedCard ? 'Pay Installment ' + nextPending.index + ' with Selected Card' : 'Pay Installment ' + nextPending.index + ' ($' + (nextPending.amount || 0).toFixed(2) + ')') : 'All Installments Paid';
                                            })()
                                            : (selectedCard ? 'Pay with Selected Card' : 'Pay Now ($' + (fullBooking.paymentTerms?.amount || 0).toFixed(2) + ')')
                                        )}
                                    </button>
                                    {paymentError && <p className="text-red-500 text-sm mt-2">{paymentError}</p>}
                                </div>
                            </div>
                        )}

                        {/* Contract Signing Section for Renters */}
                        {user && user.uid !== fullBooking.vendorId && (fullBooking.contractStatus === 'sent' || fullBooking.contractStatus === 'signed') && (
                            <div>
                                <h3 className="text-lg font-semibold mb-2">Rental Contract</h3>
                                <div className={`${fullBooking.contractStatus === 'signed' ? 'bg-green-50 border-green-400' : 'bg-blue-50 border-blue-400'} p-4 rounded-lg border-l-4`}>
                                    <p className="font-semibold">{fullBooking.contractStatus === 'signed' ? 'Contract Signed!' : 'Action Required: Contract Pending'}</p>
                                    <p className="text-sm text-gray-700 mt-1">
                                        {fullBooking.contractStatus === 'signed' 
                                            ? "You have successfully signed the rental agreement." 
                                            : "The vendor has sent a contract. Please review and sign it to proceed."}
                                    </p>
                                    <button 
                                        onClick={() => setShowContractModal(true)} 
                                        className={`w-full mt-4 text-white py-2 rounded-lg font-semibold transition ${fullBooking.contractStatus === 'signed' ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'}`}
                                    >
                                        {fullBooking.contractStatus === 'signed' ? 'View Signed Contract' : 'Review & Sign Contract'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Status Blocks for Renters */}
                        {user && user.uid !== fullBooking.vendorId && fullBooking.status === 'deposit-paid' && (
                             <div>
                                <h3 className="text-lg font-semibold mb-2">Status</h3>
                                <div className="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-400">
                                    <p className="font-semibold">Deposit Paid!</p>
                                    <p className="text-sm text-gray-700 mt-1">
                                        Your booking is secured. The vendor will request the final payment when it is due.
                                    </p>
                                </div>
                            </div>
                        )}
                        {user && user.uid !== fullBooking.vendorId && fullBooking.status === 'awaitingFinalPayment' && (
                             <div>
                                <h3 className="text-lg font-semibold mb-2">Final Payment Due</h3>
                                <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
                                    <p className="font-semibold">Your final payment is due.</p>
                                    <p className="text-sm text-gray-700 mt-1 mb-4">
                                        Please pay the remaining balance of <span className="font-bold">${(fullBooking.paymentTerms?.finalAmount || 0).toFixed(2)}</span> by <span className="font-bold">{
                                            fullBooking.paymentTerms?.finalDeadline 
                                                ? (fullBooking.paymentTerms.finalDeadline.seconds || fullBooking.paymentTerms.finalDeadline._seconds 
                                                    ? new Date((fullBooking.paymentTerms.finalDeadline.seconds || fullBooking.paymentTerms.finalDeadline._seconds) * 1000).toLocaleDateString() 
                                                    : new Date(fullBooking.paymentTerms.finalDeadline).toLocaleDateString()) 
                                                : 'the deadline'
                                        }</span>.
                                    </p>
                                    
                                    {renderCardSelection()}

                                     <button onClick={() => handlePayNow('final')} disabled={isPaying} className="w-full bg-green-500 text-white py-2 rounded-lg font-semibold hover:bg-green-600 transition disabled:bg-gray-400">
                                        {isPaying ? 'Processing...' : selectedCard ? `Pay Balance with Selected Card` : `Pay Remaining Balance ($${(fullBooking.paymentTerms?.finalAmount || 0).toFixed(2)})`}
                                    </button>
                                    {paymentError && <p className="text-red-500 text-sm mt-2">{paymentError}</p>}
                                </div>
                            </div>
                        )}
                        
                        {/* Delivery Confirmation - For Renters */}
                        {user && user.uid !== fullBooking.vendorId && fullBooking.status === 'awaitingPlannerConfirmation' && (
                             <div>
                                <h3 className="text-lg font-semibold mb-2">Delivery & Receipt</h3>
                                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                                    <p className="font-semibold">Items Delivered?</p>
                                    <p className="text-sm text-gray-700 mt-1 mb-4">
                                        The vendor has marked this order as fulfilled. Please confirm you have received everything in good condition.
                                    </p>
                                    <button onClick={() => setShowDeliveryConfirmModal(true)} className="w-full bg-blue-500 text-white py-2 rounded-lg font-semibold hover:bg-blue-600 transition">
                                        Confirm Receipt
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Request Pickup - For Renters */}
                        {user && user.uid !== fullBooking.vendorId && fullBooking.status === 'delivered' && !fullBooking.isNonReturnable && (
                             <div>
                                <h3 className="text-lg font-semibold mb-2">Rental Return</h3>
                                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                                    <p className="font-semibold">Ready for Pickup?</p>
                                    <p className="text-sm text-gray-700 mt-1 mb-4">
                                        Let the vendor know that your event is over and the items are ready to be picked up or returned.
                                    </p>
                                    <button onClick={async () => {
                                        try {
                                            await updateDoc(doc(db, "bookings", fullBooking.id), { status: 'awaitingPickup' });
                                            await createNotification(fullBooking.vendorId, `Renter is ready for pickup/return of ${fullBooking.listingTitle}.`, { page: 'bookingDetail', data: { booking: { id: fullBooking.id } } });
                                            toast.success("Vendor notified for pickup.");
                                        } catch (err) {
                                            toast.error("Failed to notify vendor.");
                                        }
                                    }} className="w-full bg-purple-500 text-white py-2 rounded-lg font-semibold hover:bg-purple-600 transition">
                                        Request Pickup / Return
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Leave a Review */}
                        {user && user.uid !== fullBooking.vendorId && canLeaveReview && (
                             <div>
                                <h3 className="text-lg font-semibold mb-2">Share Your Experience</h3>
                                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                                    <p className="font-semibold">How was your rental?</p>
                                    <p className="text-sm text-gray-700 mt-1">
                                        Let the community know about your experience by leaving a review for the vendor.
                                    </p>
                                     <button onClick={() => setShowReviewModal(true)} className="w-full mt-4 bg-purple-500 text-white py-2 rounded-lg font-semibold hover:bg-purple-600 transition">
                                        Leave a Review
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// --- SUB-COMPONENTS FOR BOOKING DETAIL ---

function BookingActions({ booking, onAcceptBooking, onDeclineBooking, onMarkAsDelivered, onOpenReturnModal, onOpenInvoiceModal, onRequestFinalPayment, onOpenContractModal }) {
    const stopPropagation = (e) => e.stopPropagation();

    return (
        <div className="flex justify-end space-x-2 flex-wrap gap-y-2">
            {!['pending', 'cancelled', 'declined'].includes(booking.status) && (
                <>
                    <button onClick={(e) => { stopPropagation(e); onOpenContractModal(booking); }} className={`px-3 py-1 rounded-lg text-sm font-semibold text-white ${booking.contractStatus === 'signed' ? 'bg-green-600 hover:bg-green-700' : booking.contractStatus === 'sent' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-600 hover:bg-gray-700'}`}>
                        {booking.contractStatus === 'signed' ? 'View Signed Contract' : booking.contractStatus === 'sent' ? 'View Sent Contract' : 'Generate Contract'}
                    </button>
                    <button onClick={(e) => { stopPropagation(e); onOpenInvoiceModal(booking); }} className="bg-gray-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-gray-700 font-semibold">
                        {booking.invoiceGeneratedAt ? 'View Invoice' : 'Generate Invoice'}
                    </button>
                </>
            )}
             {booking.status === 'pending' && (
                <>
                    <button onClick={(e) => { stopPropagation(e); onDeclineBooking(); }} className="bg-red-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-red-600 font-semibold">Decline</button>
                    <button onClick={(e) => { stopPropagation(e); onAcceptBooking(); }} className="bg-green-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-green-600 font-semibold">Accept</button>
                </>
             )}
             {booking.status === 'paid' && <button onClick={(e) => { stopPropagation(e); onMarkAsDelivered(booking); }} className="bg-blue-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-blue-600 font-semibold">Mark as Delivered</button>}
             {booking.status === 'deposit-paid' && <button onClick={(e) => { stopPropagation(e); onRequestFinalPayment(booking); }} className="bg-purple-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-purple-600 font-semibold">Request Final Payment</button>}
             {['awaitingPlannerConfirmation', 'delivered', 'awaitingPickup'].includes(booking.status) && <button onClick={(e) => { stopPropagation(e); onOpenReturnModal(booking); }} className="bg-green-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-green-600 font-semibold">Confirm Return</button>}
        </div>
    );
}

function InvoiceModal({ booking, user, onClose, db }) {
    const [notes, setNotes] = useState('');
    const [items, setItems] = useState(() => {
        if (booking.manualOrderItems) return booking.manualOrderItems;
        
        const baseItems = [
            {
                description: `${booking.listingTitle} (Rental: ${booking.date} to ${booking.returnDate})`,
                quantity: booking.quantity,
                price: (booking.subtotal ? booking.subtotal / booking.quantity : (booking.totalCost - (booking.renterFee || 0) - (booking.deliveryFee || 0) - (booking.taxAmount || 0)) / booking.quantity),
            }
        ];

        if (booking.deliveryFee && booking.deliveryFee > 0) {
            baseItems.push({
                description: 'Delivery Fee',
                quantity: 1,
                price: booking.deliveryFee
            });
        }
        
        if (booking.taxAmount && booking.taxAmount > 0) {
            baseItems.push({
                description: `Sales Tax (${((booking.taxRate || 0) * 100).toFixed(2)}%)`,
                quantity: 1,
                price: booking.taxAmount
            });
        }
        
        return baseItems;
    });
    
    const [newItem, setNewItem] = useState({ description: '', quantity: 1, price: 0 });
    const [error, setError] = useState('');

    const handleAddItem = () => {
        setError('');
        if (!newItem.description || newItem.quantity <= 0 || newItem.price < 0) {
            setError("Please fill out all fields. Quantity must be > 0.");
            return;
        }
        setItems([...items, newItem]);
        setNewItem({ description: '', quantity: 1, price: 0 });
    };

    const handleRemoveItem = (indexToRemove) => {
        setItems(items.filter((_, index) => index !== indexToRemove));
    };

    const payments = booking.payments || [];
    const totalPaid = useMemo(() => payments.reduce((acc, p) => acc + p.amount, 0), [payments]);
    const subtotal = useMemo(() => items.reduce((acc, item) => acc + (item.quantity * item.price), 0), [items]);

    const renterFee = booking.renterFee || 0;
    const totalCost = subtotal + renterFee;
    const balanceDue = useMemo(() => totalCost - totalPaid, [totalCost, totalPaid]);

    const bookingNumber = booking.bookingNumber || `BK-${booking.id.slice(0, 6).toUpperCase()}`;
    const issueDate = new Date().toLocaleDateString();
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 30); 

    const handleGeneratePdf = async () => {
        const printWindow = window.open('', '_blank');
        const itemsHtml = items.map(item => `
            <tr class="border-b">
                <td class="p-3">${item.description}</td>
                <td class="p-3 text-center">${item.quantity}</td>
                <td class="p-3 text-right">$${parseFloat(item.price).toFixed(2)}</td>
                <td class="p-3 text-right">$${(item.quantity * item.price).toFixed(2)}</td>
            </tr>
        `).join('');

        const paymentsHtml = payments.map(p => `
            <div class="flex justify-between text-gray-700">
                <span class="font-semibold">Payment Received (${new Date(p.date.seconds * 1000).toLocaleDateString()}) - ${p.type}</span>
                <span>-$${parseFloat(p.amount).toFixed(2)}</span>
            </div>
        `).join('');

        if (db && booking.id && !booking.invoiceGeneratedAt) {
            const bookingRef = doc(db, "bookings", booking.id);
            try {
                await updateDoc(bookingRef, { invoiceGeneratedAt: serverTimestamp() });
            } catch (error) {
                console.error("Failed to mark invoice as generated:", error);
            }
        }

        printWindow.document.write(`
            <html>
                <head>
                    <title>Invoice ${bookingNumber}</title>
                    <script src="https://cdn.tailwindcss.com"></script>
                    <style> @media print { body { -webkit-print-color-adjust: exact; } .no-print { display: none; } } </style>
                </head>
                <body class="font-sans p-10 bg-white">
                    <div>
                        <div class="flex justify-between items-start mb-10">
                            <div>
                                <h1 class="text-4xl font-bold text-gray-800">INVOICE</h1>
                                <p class="text-gray-500">${bookingNumber}</p>
                            </div>
                            <div class="text-right">
                                ${user.profilePicUrl ? `<img src="${user.profilePicUrl}" alt="Vendor Logo" class="w-20 h-20 rounded-full object-cover ml-auto mb-2">` : ''}
                                <h2 class="text-2xl font-semibold text-gray-700">${user.vendorName || user.email}</h2>
                                <p class="text-gray-500">${user.email}</p>
                            </div>
                        </div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-10 mb-10">
                            <div>
                                <h3 class="text-sm font-semibold text-gray-500 uppercase mb-2">Bill To</h3>
                                <p class="font-bold text-gray-800">${booking.renterName || 'Guest'}</p>
                                <p class="text-gray-600">${booking.renterEmail}</p>
                            </div>
                            <div class="text-right">
                                <p class="font-bold text-gray-800">Date Issued: ${issueDate}</p>
                                <p class="text-gray-600">Due Date: ${dueDate.toLocaleDateString()}</p>
                            </div>
                        </div>

                        <table class="w-full mb-10 border-collapse">
                            <thead>
                                <tr class="bg-gray-100 border-b border-gray-300">
                                    <th class="text-left p-3 font-semibold text-gray-600">Description</th>
                                    <th class="text-center p-3 font-semibold text-gray-600">Quantity</th>
                                    <th class="text-right p-3 font-semibold text-gray-600">Price</th>
                                    <th class="text-right p-3 font-semibold text-gray-600">Amount</th>
                                </tr>
                            </thead>
                            <tbody>${itemsHtml}</tbody>
                        </table>

                        <div class="flex justify-end mb-10">
                            <div class="w-1/2">
                                <div class="flex justify-between border-b pb-2 mb-2">
                                    <span class="font-semibold text-gray-700">Subtotal</span>
                                    <span>$${subtotal.toFixed(2)}</span>
                                </div>
                                <div class="flex justify-between font-bold text-xl text-gray-800">
                                    <span>Total</span>
                                    <span>$${totalCost.toFixed(2)}</span>
                                </div>
                                <div class="mt-6 border-t pt-4">
                                    <h4 class="font-bold text-gray-700 mb-2">Payment History</h4>
                                    ${paymentsHtml || '<p class="text-gray-500 italic">No payments recorded.</p>'}
                                    <div class="flex justify-between font-bold text-lg text-gray-800 mt-4 border-t border-gray-300 pt-2">
                                        <span>Balance Due</span>
                                        <span>$${balanceDue.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="border-t pt-8">
                            <h4 class="font-bold text-gray-800 mb-2">Notes</h4>
                            <p class="text-gray-600">${notes || 'Thank you for your business!'}</p>
                        </div>
                        <div class="no-print mt-10 text-center">
                            <button onclick="window.print()" class="bg-gray-800 text-white px-6 py-2 rounded font-bold hover:bg-gray-700">Print / Save as PDF</button>
                        </div>
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
            <div className="bg-white p-4 md:p-8 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl md:text-2xl font-bold">Invoice & Payments</h2>
                    <div className="space-x-2 flex">
                        <button onClick={handleGeneratePdf} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700">Download PDF</button>
                        <button onClick={onClose} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-semibold hover:bg-gray-300">Close</button>
                    </div>
                </div>
                
                {user.uid === booking.vendorId && (
                    <div className="mb-8 p-4 bg-gray-50 rounded-lg border">
                        <h3 className="font-bold mb-4">Edit Invoice Items</h3>
                        {error && <p className="text-red-500 text-sm mb-3 font-semibold">{error}</p>}
                        <div className="space-y-2 mb-4">
                            {items.map((item, index) => (
                                <div key={index} className="flex gap-2 items-center">
                                    <input type="text" value={item.description} onChange={(e) => { const newItems = [...items]; newItems[index].description = e.target.value; setItems(newItems); }} className="flex-grow p-2 border rounded" />
                                    <input type="number" value={item.quantity} onChange={(e) => { const newItems = [...items]; newItems[index].quantity = parseInt(e.target.value); setItems(newItems); }} className="w-20 p-2 border rounded" />
                                    <input type="number" value={item.price} onChange={(e) => { const newItems = [...items]; newItems[index].price = parseFloat(e.target.value); setItems(newItems); }} className="w-24 p-2 border rounded" />
                                    <button onClick={() => handleRemoveItem(index)} className="text-red-500 font-bold px-2">×</button>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2 items-end">
                            <div className="flex-grow">
                                <label className="text-xs">Description</label>
                                <input type="text" value={newItem.description} onChange={(e) => setNewItem({...newItem, description: e.target.value})} className="w-full p-2 border rounded" />
                            </div>
                            <div className="w-20">
                                <label className="text-xs">Qty</label>
                                <input type="number" value={newItem.quantity} onChange={(e) => setNewItem({...newItem, quantity: parseInt(e.target.value)})} className="w-full p-2 border rounded" />
                            </div>
                            <div className="w-24">
                                <label className="text-xs">Price</label>
                                <input type="number" value={newItem.price} onChange={(e) => setNewItem({...newItem, price: parseFloat(e.target.value)})} className="w-full p-2 border rounded" />
                            </div>
                            <button onClick={handleAddItem} className="bg-green-500 text-white px-4 py-2 rounded font-semibold h-[42px]">+</button>
                        </div>
                    </div>
                )}

                <div className="flex justify-end">
                    <div className="w-1/2 text-right space-y-2">
                        <div className="flex justify-between"><span className="text-gray-600">Subtotal:</span> <span className="font-semibold">${subtotal.toFixed(2)}</span></div>
                        <div className="flex justify-between text-xl font-bold"><span className="text-gray-800">Total:</span> <span>${totalCost.toFixed(2)}</span></div>
                        <div className="flex justify-between text-green-600"><span className="">Paid:</span> <span>-${totalPaid.toFixed(2)}</span></div>
                        <div className="flex justify-between text-red-600 font-bold border-t pt-2"><span className="">Balance Due:</span> <span>${balanceDue.toFixed(2)}</span></div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function RichTextEditor({ initialContent, onChange, isReadOnly, placeholder }) {
    const editorRef = useRef(null);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        if (editorRef.current && !isMounted) {
            editorRef.current.innerHTML = initialContent;
            setIsMounted(true);
        }
    }, [initialContent, isMounted]);

    const handleInput = () => {
        const html = editorRef.current.innerHTML;
        onChange(html);
    };

    const execCmd = (cmd, arg) => {
        document.execCommand(cmd, false, arg);
        editorRef.current.focus();
    };

    if (isReadOnly) return <div className="whitespace-pre-wrap font-serif text-gray-800 leading-relaxed p-4 border rounded bg-gray-50" dangerouslySetInnerHTML={{__html: initialContent}} />;

    return (
        <div className="flex flex-col h-96 border rounded-lg border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-pink-500 focus-within:border-transparent transition-all">
            <div className="bg-gray-50 border-b border-gray-200 p-2 flex gap-1 flex-wrap items-center">
                <button onClick={(e) => { e.preventDefault(); execCmd('bold'); }} className="p-1.5 hover:bg-gray-200 rounded text-gray-700 font-bold w-8 text-center">B</button>
                <button onClick={(e) => { e.preventDefault(); execCmd('italic'); }} className="p-1.5 hover:bg-gray-200 rounded text-gray-700 italic w-8 text-center">I</button>
                <button onClick={(e) => { e.preventDefault(); execCmd('underline'); }} className="p-1.5 hover:bg-gray-200 rounded text-gray-700 underline w-8 text-center">U</button>
                <div className="w-px h-6 bg-gray-300 mx-1"></div>
                <button onClick={(e) => { e.preventDefault(); execCmd('formatBlock', 'H2'); }} className="p-1.5 hover:bg-gray-200 rounded text-gray-700 font-bold text-sm px-2">H2</button>
                <button onClick={(e) => { e.preventDefault(); execCmd('formatBlock', 'H3'); }} className="p-1.5 hover:bg-gray-200 rounded text-gray-700 font-bold text-sm px-2">H3</button>
            </div>
            <div
                ref={editorRef}
                className="flex-grow p-4 overflow-y-auto outline-none font-serif text-gray-800 leading-relaxed"
                contentEditable
                onInput={handleInput}
                data-placeholder={placeholder}
            />
        </div>
    );
}

function ContractModal({ booking, user, onClose, onSend, onSign }) {
    const [content, setContent] = useState(booking.contractHtml || '');
    const [signature, setSignature] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [useAi, setUseAi] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    useEffect(() => {
        if (user.uid === booking.vendorId && !content && !booking.contractHtml) {
            let defaultText = user.rentalAgreement ? user.rentalAgreement.replace(/\n/g, '<br/>') : '';
            if (!defaultText) {
                defaultText = `
                    <h2 style="text-align: center;">RENTAL AGREEMENT</h2>
                    <p>This contract is between <strong>${user.vendorName || user.email}</strong> (Vendor) and <strong>${booking.renterName || booking.renterEmail}</strong> (Renter).</p>
                    <hr/>
                    <h3>Booking Details</h3>
                    <p><strong>Booking Ref:</strong> ${booking.bookingNumber || booking.id}<br/>
                    <strong>Item(s):</strong> ${booking.quantity} x ${booking.listingTitle}<br/>
                    <strong>Dates:</strong> ${booking.date} to ${booking.returnDate}<br/>
                    <strong>Total Cost:</strong> $${(booking.totalCost || 0).toFixed(2)}</p>
                    <hr/>
                    <h3>TERMS AND CONDITIONS</h3>
                    <ol>
                        <li>The Renter agrees to return the items in the same condition as received.</li>
                        <li>The Renter is responsible for any damage or loss.</li>
                        <li>Payment is due as per the terms specified in the booking.</li>
                        <li>Cancellation policies apply as set by the Vendor.</li>
                    </ol>
                    <p><em>[You can edit this text before sending]</em></p>
                `;
            }
            setContent(defaultText);
        }
    }, [user, booking, content]);

    const handleGenerateAiContract = async () => {
        setIsGenerating(true);
        setError('');
        try {
            const prompt = `Write a professional, comprehensive, and legally-sound rental contract for a party rental transaction.
            **Transaction Details:**
            - **Vendor (Owner):** ${user.vendorName || user.email}
            - **Renter (Customer):** ${booking.renterName || booking.renterEmail}
            - **Item(s) Rented:** ${booking.quantity} x ${booking.listingTitle}
            - **Rental Period:** ${booking.date} to ${booking.returnDate}
            - **Total Transaction Value:** $${(booking.totalCost || 0).toFixed(2)}
            
            Use HTML formatting tags. Do NOT use markdown.`;

            const payload = { contents: [{ role: "user", parts: [{ text: prompt }] }] };
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${GEMINI_API_KEY}`;
            const response = await fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const result = await response.json();
            
            if (result.candidates?.[0]) {
                setContent(result.candidates[0].content.parts[0].text);
            } else {
                throw new Error("AI response was empty.");
            }
        } catch (err) {
            setError("Failed to generate contract with AI. Please try again.");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleDownloadPdf = () => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Contract - ${booking.bookingNumber}</title>
                    <style>
                        body { font-family: sans-serif; line-height: 1.6; padding: 40px; max-width: 800px; margin: 0 auto; color: #1f2937; }
                        .header { display: flex; justify-content: space-between; border-bottom: 2px solid #e5e7eb; padding-bottom: 20px; margin-bottom: 30px; }
                        .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 40px; background: #f9fafb; padding: 25px; border-radius: 8px; border: 1px solid #e5e7eb; }
                        @media print { .no-print { display: none; } body { padding: 0; } }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <div><h1>Rental Agreement</h1></div>
                        <div><p><strong>Booking Ref:</strong> ${booking.bookingNumber}</p></div>
                    </div>
                    <div class="parties">
                        <div><h3>Vendor</h3><p>${user.isVendor ? (user.vendorName || user.email) : 'Vendor'}</p></div>
                        <div><h3>Renter</h3><p>${booking.renterName || booking.renterEmail}</p></div>
                    </div>
                    <div>${content}</div>
                    ${booking.contractStatus === 'signed' ? `
                        <div style="margin-top: 40px; padding: 20px; background: #ecfdf5; border-left: 4px solid #059669;">
                            <strong>Digitally Signed by ${booking.contractSignature}</strong><br/>
                            <span>Timestamp: ${booking.contractSignedAt?.toDate ? booking.contractSignedAt.toDate().toLocaleString() : new Date().toLocaleString()}</span>
                        </div>
                    ` : ''}
                    <div class="no-print" style="margin-top: 30px; text-align: center;">
                        <button onclick="window.print()" style="padding: 12px 24px; background: #111827; color: white; border: none; border-radius: 8px; cursor: pointer;">Download PDF / Print</button>
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleSend = async () => {
        if (!content.trim()) { setError("Contract content cannot be empty."); return; }
        setLoading(true);
        try {
            await onSend(content);
            onClose();
        } catch (e) {
            setError("Failed to send contract.");
            setLoading(false);
        } 
    };

    const handleSign = async () => {
        if (!signature.trim()) { setError("Please type your full name to sign."); return; }
        setLoading(true);
        try {
            await onSign(signature);
            onClose();
        } catch (e) {
            setError("Failed to sign contract.");
            setLoading(false);
        }
    };

    const isVendor = user.uid === booking.vendorId;
    const isSigned = booking.contractStatus === 'signed';
    const isSent = booking.contractStatus === 'sent';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
            <div className="bg-white p-4 md:p-8 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
                    <h2 className="text-xl md:text-2xl font-bold">Rental Contract</h2>
                    <span className={`px-3 py-1 rounded-full text-sm font-bold w-fit ${isSigned ? 'bg-green-100 text-green-800' : isSent ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-800'}`}>
                        Status: {isSigned ? 'Signed' : isSent ? 'Sent to Renter' : 'Draft'}
                    </span>
                </div>
                
                {isVendor && !isSent && !isSigned && (
                    <div className="mb-4 flex items-center justify-between bg-purple-50 p-3 rounded-lg border border-purple-100">
                        <label className="flex items-center">
                            <input type="checkbox" checked={useAi} onChange={(e) => setUseAi(e.target.checked)} className="h-4 w-4 text-purple-600 rounded" />
                            <span className="ml-2 text-sm font-medium text-gray-800">Use AI Assistant</span>
                        </label>
                        {useAi && (
                            <button onClick={handleGenerateAiContract} disabled={isGenerating} className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 disabled:opacity-50">
                                {isGenerating ? 'Generating...' : '✨ Generate Draft'}
                            </button>
                        )}
                    </div>
                )}

                <div className="flex-grow overflow-y-auto mb-6">
                    {isVendor && !isSent && !isSigned ? (
                        <RichTextEditor initialContent={content} onChange={setContent} placeholder={isGenerating ? "AI is writing..." : "Enter contract terms here..."} />
                    ) : (
                        <div className="whitespace-pre-wrap font-serif text-gray-800 leading-relaxed border p-6 rounded bg-gray-50 shadow-inner" dangerouslySetInnerHTML={{__html: content || booking.contractHtml || "No contract content available."}} />
                    )}
                </div>

                {error && <p className="text-red-500 mb-4 font-semibold">{error}</p>}

                {isSigned && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded flex items-center gap-4">
                        <div className="bg-green-500 text-white rounded-full p-2">✓</div>
                        <div>
                            <p className="text-green-900 font-bold text-lg">Digitally Signed by {booking.contractSignature}</p>
                            <p className="text-sm text-green-700">Date: {booking.contractSignedAt?.toDate ? booking.contractSignedAt.toDate().toLocaleString() : new Date().toLocaleString()}</p>
                        </div>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4 border-t">
                    <button onClick={handleDownloadPdf} className="flex items-center justify-center gap-2 px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 font-semibold transition">
                        Download PDF
                    </button>

                    <div className="flex gap-3 justify-end w-full sm:w-auto">
                        <button onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold transition">Close</button>
                        
                        {isVendor && !isSent && !isSigned && (
                            <button onClick={handleSend} disabled={loading || isGenerating} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition disabled:bg-blue-400">
                                {loading ? 'Sending...' : 'Send to Renter'}
                            </button>
                        )}
                        
                        {!isVendor && isSent && !isSigned && (
                            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                                <input 
                                    type="text" 
                                    placeholder="Type Full Name to Sign" 
                                    value={signature} 
                                    onChange={(e) => setSignature(e.target.value)}
                                    className="border p-2 rounded-lg flex-grow min-w-[200px] outline-none"
                                />
                                <button onClick={handleSign} disabled={loading} className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition disabled:bg-green-400">
                                    {loading ? 'Signing...' : 'Sign & Accept'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function AcceptBookingModal({ booking, onClose, onConfirm }) {
    const [paymentType, setPaymentType] = useState('full');
    const [depositPercentage, setDepositPercentage] = useState(50);
    const [deadlineDays, setDeadlineDays] = useState(2);
    const [error, setError] = useState('');

    const [installments, setInstallments] = useState([
        { id: 1, amount: ((booking?.totalCost || 0) / 2).toFixed(2), deadlineDays: 2 },
        { id: 2, amount: ((booking?.totalCost || 0) / 2).toFixed(2), deadlineDays: 30 }
    ]);

    const handleAddInstallment = () => setInstallments([...installments, { id: Date.now(), amount: '', deadlineDays: 30 }]);
    const handleRemoveInstallment = (id) => setInstallments(installments.filter(i => i.id !== id));
    const handleInstallmentChange = (id, field, value) => setInstallments(installments.map(i => i.id === id ? { ...i, [field]: value } : i));

    const depositAmount = useMemo(() => {
        if (!booking || paymentType !== 'deposit') return 0;
        return ((booking.totalCost || 0) * (depositPercentage / 100)).toFixed(2);
    }, [booking, paymentType, depositPercentage]);

    const handleConfirm = () => {
        if (paymentType !== 'no_payment' && paymentType !== 'installments' && deadlineDays <= 0) {
            setError('Payment deadline must be at least 1 day.');
            return;
        }

        const deadline = new Date();
        deadline.setDate(deadline.getDate() + parseInt(deadlineDays, 10));

        let paymentTerms = {};

        if (paymentType === 'installments') {
            const totalInst = installments.reduce((acc, i) => acc + parseFloat(i.amount || 0), 0);
            if (Math.abs(totalInst - (booking?.totalCost || 0)) > 0.02) {
                setError("Installment amounts must equal the total cost ($" + (booking?.totalCost || 0).toFixed(2) + ").");
                return;
            }
            paymentTerms = {
                type: 'installments',
                deadline: new Date(Date.now() + parseInt(installments[0].deadlineDays, 10) * 24 * 60 * 60 * 1000),
                schedule: installments.map((inst, index) => {
                    const d = new Date();
                    d.setDate(d.getDate() + parseInt(inst.deadlineDays, 10));
                    return { id: inst.id, index: index + 1, amount: parseFloat(inst.amount), deadline: d, status: 'pending' };
                })
            };
        } else {
            paymentTerms = {
                type: paymentType,
                amount: paymentType === 'full' ? booking.totalCost : (paymentType === 'deposit' ? parseFloat(depositAmount) : 0),
                deadline: paymentType === 'no_payment' ? null : deadline,
            };
        }
        onConfirm(booking, paymentTerms);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
            <div className="bg-white p-4 md:p-8 rounded-lg shadow-xl max-w-md w-full">
                <h2 className="text-xl font-bold mb-4">Accept Booking & Request Payment</h2>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Payment Request Type</label>
                        <select value={paymentType} onChange={(e) => setPaymentType(e.target.value)} className="mt-1 w-full p-2 border rounded-md">
                            <option value="full">Request Full Payment (${(booking?.totalCost || 0).toFixed(2)})</option>
                            <option value="deposit">Request a Deposit</option>
                            <option value="installments">Payment Schedule / Installments</option>
                            <option value="no_payment">Accept (No Payment Required)</option>
                        </select>
                    </div>

                    {paymentType === 'installments' && (
                        <div className="space-y-4 border-t pt-4 mt-4">
                            <label className="block text-sm font-medium text-gray-700">Payment Schedule</label>
                            {installments.map((inst, idx) => (
                                <div key={inst.id} className="flex gap-2 items-center bg-gray-50 p-2 rounded-md">
                                    <span className="text-xs font-bold text-gray-400 w-4">{idx + 1}.</span>
                                    <div className="flex-1">
                                        <div className="relative">
                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                            <input type="number" value={inst.amount} onChange={(e) => handleInstallmentChange(inst.id, 'amount', e.target.value)} className="w-full pl-6 p-1.5 border rounded text-sm" />
                                        </div>
                                    </div>
                                    <div className="flex-1 flex items-center gap-1">
                                        <input type="number" value={inst.deadlineDays} onChange={(e) => handleInstallmentChange(inst.id, 'deadlineDays', e.target.value)} className="w-16 p-1.5 border rounded text-sm" />
                                        <span className="text-xs text-gray-500">days</span>
                                    </div>
                                    <button onClick={() => handleRemoveInstallment(inst.id)} className="text-red-500 hover:text-red-700 p-1">✕</button>
                                </div>
                            ))}
                            <div className="flex justify-between items-center">
                                <button onClick={handleAddInstallment} className="text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-md font-bold">+ Add Payment</button>
                                <span className={`text-xs font-bold ${installments.reduce((a, b) => a + parseFloat(b.amount || 0), 0) === (booking?.totalCost || 0) ? 'text-green-600' : 'text-red-500'}`}>
                                    Total: ${installments.reduce((a, b) => a + parseFloat(b.amount || 0), 0).toFixed(2)} / ${(booking?.totalCost || 0).toFixed(2)}
                                </span>
                            </div>
                        </div>
                    )}

                    {paymentType !== 'no_payment' && paymentType !== 'installments' && (
                        <div className="space-y-4">
                             {paymentType === 'deposit' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Deposit Amount</label>
                                    <div className="flex items-center space-x-2">
                                        <input type="range" min="10" max="100" value={depositPercentage} onChange={(e) => setDepositPercentage(e.target.value)} className="w-full" />
                                        <span className="font-semibold">{depositPercentage}%</span>
                                    </div>
                                    <p className="text-center font-bold text-lg text-pink-600">${depositAmount}</p>
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Payment Deadline (Days from now)</label>
                                <input type="number" value={deadlineDays} onChange={(e) => setDeadlineDays(e.target.value)} min="1" className="w-full p-2 border rounded-md mt-1" />
                            </div>
                        </div>
                    )}
                </div>
                {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
                <div className="mt-6 flex justify-end space-x-4">
                    <button onClick={onClose} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-semibold hover:bg-gray-300">Cancel</button>
                    <button onClick={handleConfirm} className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700">Confirm</button>
                </div>
            </div>
        </div>
    );
}

function LeaveReviewModal({ booking, onClose, user, db }) {
    const [rating, setRating] = useState(5);
    const [text, setText] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!text.trim()) { setError("Please write a review."); return; }
        setLoading(true);
        setError('');

        try {
            await addDoc(collection(db, 'reviews'), {
                listingId: booking.listingId,
                vendorId: booking.vendorId,
                renterId: user.uid,
                renterName: user.plannerName || user.firstName || user.email || 'Guest',
                rating,
                text,
                createdAt: serverTimestamp(),
            });

            await updateDoc(doc(db, 'bookings', booking.id), { reviewLeft: true });
            onClose();
        } catch (err) {
            setError("Failed to submit review.");
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-lg shadow-2xl max-w-md w-full">
                <h2 className="text-2xl font-bold mb-4">Leave a Review</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="flex justify-center space-x-1">
                        {[...Array(5)].map((_, index) => {
                            const ratingValue = index + 1;
                            return (
                                <button type="button" key={ratingValue} onClick={() => setRating(ratingValue)} className="focus:outline-none">
                                    <svg className={`w-8 h-8 ${ratingValue <= rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                </button>
                            );
                        })}
                    </div>
                    <div>
                        <textarea value={text} onChange={(e) => setText(e.target.value)} rows="4" className="w-full p-2 border border-gray-300 rounded-md" placeholder="Tell us about your experience..."></textarea>
                    </div>
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <div className="mt-6 flex justify-end space-x-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300">Cancel</button>
                        <button type="submit" disabled={loading} className="px-4 py-2 rounded-lg bg-purple-500 text-white hover:bg-purple-600 disabled:bg-gray-400">
                            {loading ? 'Submitting...' : 'Submit Review'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function DeliveryConfirmationModal({ booking, onClose, onConfirm }) {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-lg shadow-2xl max-w-md w-full">
                <h2 className="text-2xl font-bold mb-4">Confirm Delivery</h2>
                <p className="mb-4 text-gray-600">Please confirm that you have received {booking.quantity} x {booking.listingTitle} and they are in good condition.</p>
                <div className="mt-6 flex justify-end space-x-4">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300">Cancel</button>
                    <button onClick={onConfirm} className="px-4 py-2 rounded-lg bg-green-500 text-white hover:bg-green-600">Confirm Delivery</button>
                </div>
            </div>
        </div>
    );
}

function ReturnConfirmationModal({ booking, onClose, db }) {
    const [loading, setLoading] = useState(false);
    const handleConfirm = async () => {
        setLoading(true);
        try {
            await updateDoc(doc(db, "bookings", booking.id), { status: 'returned' });
            await createNotification(booking.renterId, `Vendor confirmed return of ${booking.listingTitle}.`, { page: 'bookingDetail', data: { booking: { id: booking.id } } });
            onClose();
        } catch (err) {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
            <div className="bg-white p-8 rounded-lg shadow-2xl max-w-md w-full">
                <h2 className="text-2xl font-bold mb-4">Confirm Return</h2>
                <p className="mb-6 text-gray-600">Please confirm that you have received {booking.quantity} x {booking.listingTitle} back from the renter.</p>
                <div className="mt-6 flex justify-end space-x-4">
                    <button onClick={onClose} disabled={loading} className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300">Cancel</button>
                    <button onClick={handleConfirm} disabled={loading} className="px-4 py-2 rounded-lg bg-green-500 text-white hover:bg-green-600 disabled:bg-gray-400">
                        {loading ? 'Confirming...' : 'Confirm Return'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function PaymentReceiptModal({ booking, onClose }) {
    const latestPayment = booking.payments && booking.payments.length > 0 
        ? [...booking.payments].sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0))[0] 
        : null;

    const amount = latestPayment ? latestPayment.amount : booking.paymentTerms?.amount || 0;
    const paymentDate = latestPayment?.date?.seconds 
        ? new Date(latestPayment.date.seconds * 1000) 
        : new Date();
    
    const totalPaid = (booking.payments || []).reduce((acc, p) => acc + p.amount, 0);
    const balanceDue = (booking.totalCost || 0) - totalPaid;
    const bookingNumber = booking.bookingNumber || booking.id.slice(0, 8).toUpperCase();
    
    const isFullyPaid = balanceDue <= 0.01;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[200] p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-center text-white">
                    <div className="flex justify-center mb-4">
                        <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                            <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                    </div>
                    <h2 className="text-3xl font-bold mb-2">Payment Confirmed!</h2>
                    <p className="text-green-100 font-medium">Your payment has been successfully processed</p>
                </div>

                {/* Content */}
                <div className="p-8 space-y-6">
                    {/* Confirmation Number */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <p className="text-xs text-gray-600 font-semibold uppercase tracking-wide mb-1">Confirmation Number</p>
                        <p className="text-2xl font-black text-gray-900">{bookingNumber}</p>
                    </div>

                    {/* Payment Details */}
                    <div className="space-y-3">
                        <h3 className="font-bold text-gray-900">Payment Details</h3>
                        <div className="space-y-2 bg-gray-50 rounded-lg p-4">
                            <div className="flex justify-between items-center py-2">
                                <span className="text-gray-700">Amount Paid</span>
                                <span className="text-2xl font-black text-gray-900">${parseFloat(amount).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 text-sm">
                                <span className="text-gray-600">Payment Date</span>
                                <span className="text-gray-900 font-medium">{paymentDate.toLocaleDateString()} at {paymentDate.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</span>
                            </div>
                            {latestPayment?.type && (
                                <div className="flex justify-between items-center py-2 text-sm">
                                    <span className="text-gray-600">Payment Type</span>
                                    <span className="text-gray-900 font-medium capitalize">{latestPayment.type}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Booking Summary */}
                    <div className="space-y-3">
                        <h3 className="font-bold text-gray-900">Order Summary</h3>
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100 space-y-2">
                            <div className="flex justify-between items-start">
                                <span className="text-gray-700">{booking.quantity}x {booking.listingTitle}</span>
                                <span className="text-gray-900 font-semibold text-right">${(booking.subtotal || (booking.totalCost - (booking.renterFee || 0) - (booking.deliveryFee || 0) - (booking.taxAmount || 0))).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm text-gray-600 py-1 border-t border-blue-100">
                                <span>Rental Period</span>
                                <span className="font-medium">{booking.date} to {booking.returnDate}</span>
                            </div>
                        </div>
                    </div>

                    {/* Payment Status */}
                    <div className={`rounded-lg p-4 ${isFullyPaid ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                        {isFullyPaid ? (
                            <p className="text-green-900 font-semibold">✓ Payment Complete - Your booking is all set!</p>
                        ) : (
                            <>
                                <p className="text-orange-900 font-semibold mb-1">Balance Remaining</p>
                                <p className="text-orange-800 text-sm">An additional payment of <span className="font-bold">${balanceDue.toFixed(2)}</span> will be requested by the vendor.</p>
                            </>
                        )}
                    </div>

                    {/* CTA Button */}
                    <button 
                        onClick={onClose} 
                        className="w-full bg-gray-900 text-white py-4 rounded-xl font-bold hover:bg-gray-800 transition-colors shadow-md text-lg"
                    >
                        Continue to Booking
                    </button>

                    {/* Footer Info */}
                    <p className="text-xs text-gray-500 text-center">A confirmation email has been sent to your registered email address.</p>
                </div>
            </div>
        </div>
    );
}

function InsuranceClaimModal({ booking, onClose, user, db, storage }) {
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!description.trim()) return;
        setIsSubmitting(true);
        try {
            await updateDoc(doc(db, "bookings", booking.id), {
                claimStatus: 'submitted',
                claimDate: serverTimestamp(),
                claimDetails: { description, submittedBy: user.uid }
            });
            onClose();
        } catch (err) {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[300] p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 md:p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6">File Insurance Claim</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Description of Damage</label>
                        <textarea required value={description} onChange={(e) => setDescription(e.target.value)} rows="4" className="w-full p-3 border rounded-lg focus:ring-2 outline-none resize-none" />
                    </div>
                    <div className="flex justify-end gap-3 mt-2">
                        <button type="button" onClick={onClose} disabled={isSubmitting} className="px-5 py-2.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50">Cancel</button>
                        <button type="submit" disabled={isSubmitting || !description.trim()} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50">
                            {isSubmitting ? 'Submitting...' : 'Submit Claim'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}